import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types for visualization data structures
export interface TargetRankingData {
  targets: TargetRankingItem[];
  maxScore: number;
  maxConfidence: number;
}

export interface TargetRankingItem {
  id: string;
  name: string;
  probabilityScore: number;
  confidenceLevel: number;
  estimatedSize: number; // km²
  explorationStage: 'drill_ready' | 'needs_work' | 'early_stage';
  commodity: string;
  alterationType: string;
  nearestAnalog: string;
}

export interface SpatialPriorityData {
  probabilityGrid: Float32Array;
  confidenceGrid: Float32Array;
  gridDimensions: [number, number];
  bounds: [number, number, number, number]; // [minLat, minLon, maxLat, maxLon]
  targets: HighPriorityZone[];
  structuralFeatures: StructuralFeature[];
  knownDeposits: KnownDeposit[];
}

export interface HighPriorityZone {
  id: string;
  center: [number, number];
  area: number;
  maxScore: number;
  priority: number;
  confidence: number;
}

export interface StructuralFeature {
  id: string;
  type: 'fault' | 'lineament' | 'intrusion';
  geometry: GeoJSON.LineString | GeoJSON.Point;
  importance: number;
  orientation?: number;
}

export interface KnownDeposit {
  id: string;
  name: string;
  location: [number, number];
  status: 'active' | 'past_producer' | 'prospect';
  commodity: string;
}

export interface AnomalyLayer {
  id: string;
  name: string;
  type: 'spectral' | 'geochemical' | 'geophysical' | 'structural';
  data: Float32Array;
  colorScale: string;
  valueRange: [number, number];
  opacity: number;
  visible: boolean;
  metadata: {
    units: string;
    description: string;
    quality: 'high' | 'medium' | 'low';
  };
}

export interface GeologicalContext {
  regionalGeology: RegionalGeology;
  depositAnalogs: DepositAnalog[];
  stratigraphicColumn: StratUnit[];
  structuralInterpretation: StructuralModel;
}

export interface RegionalGeology {
  province: string;
  tectonicSetting: string;
  dominantLithology: string;
  ageRange: string;
  metallogenicBelt: string;
}

export interface DepositAnalog {
  id: string;
  name: string;
  country: string;
  productionOz: number;
  grade: number;
  similarityScore: number;
  keyFeatures: string[];
}

export interface StratUnit {
  name: string;
  thickness: number;
  age: string;
  lithology: string;
}

export interface StructuralModel {
  dominantStructures: StructuralFeature[];
  stressField: string;
  intersectionZones: [number, number][];
}

export interface QualityMetrics {
  dataCompleteness: number;
  reliability: number;
  confidence: number;
  lastUpdate: string;
}

export interface ProcessingMetadata {
  algorithms: string[];
  processingTime: number;
  memoryUsage: number;
  version: string;
}

export interface ServiceStatus {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number;
  lastCheck: string;
}

export interface FilterConfiguration {
  commodityFilter: string[];
  probabilityRange: [number, number];
  confidenceThreshold: number;
  showOnlyHighPriority: boolean;
  analysisTypes: string[];
}

// Main state interface
interface ReportVisualizationState {
  // View Configuration
  viewMode: 'table' | 'executive' | 'technical' | 'geological';
  selectedAnalysisId: string | null;
  comparisonAnalysisIds: string[];
  
  // Report state persistence
  showDetailedReport: boolean;
  selectedAnalysisData: any | null;
  analysisDetails: any | null;
  
  // Data Processing
  processedData: {
    targetRanking: TargetRankingData | null;
    spatialPriority: SpatialPriorityData | null;
    anomalyLayers: Map<string, AnomalyLayer>;
    geologicalContext: GeologicalContext | null;
  };
  
  // Loading states
  loading: {
    targetRanking: boolean;
    spatialPriority: boolean;
    anomalyLayers: boolean;
    geologicalContext: boolean;
  };
  
  // Error states
  errors: {
    targetRanking: string | null;
    spatialPriority: string | null;
    anomalyLayers: string | null;
    geologicalContext: string | null;
  };
  
  // Visualization Settings
  settings: {
    layerOpacity: Record<string, number>;
    visibleLayers: string[];
    colorScheme: 'default' | 'colorblind' | 'highContrast';
    dataResolution: 'low' | 'medium' | 'high';
  };
  
  // Performance
  cache: {
    lastFetch: number;
    cachedAnalysisIds: string[];
    cacheTimeout: number; // milliseconds
  };
  
  // UI State
  expandedPanels: string[];
  activeFilters: FilterConfiguration;
  exportInProgress: boolean;
  
  // Metadata
  qualityMetrics: QualityMetrics | null;
  processingInfo: ProcessingMetadata | null;
  serviceHealth: ServiceStatus[];
}

// Initial state
const initialState: ReportVisualizationState = {
  viewMode: 'executive',  // Changed from 'table' to 'executive' as default for visualization
  selectedAnalysisId: null,
  comparisonAnalysisIds: [],
  
  // Report persistence
  showDetailedReport: false,
  selectedAnalysisData: null,
  analysisDetails: null,
  
  processedData: {
    targetRanking: null,
    spatialPriority: null,
    anomalyLayers: new Map(),
    geologicalContext: null,
  },
  
  loading: {
    targetRanking: false,
    spatialPriority: false,
    anomalyLayers: false,
    geologicalContext: false,
  },
  
  errors: {
    targetRanking: null,
    spatialPriority: null,
    anomalyLayers: null,
    geologicalContext: null,
  },
  
  settings: {
    layerOpacity: {},
    visibleLayers: [],
    colorScheme: 'default',
    dataResolution: 'medium',
  },
  
  cache: {
    lastFetch: 0,
    cachedAnalysisIds: [],
    cacheTimeout: 300000, // 5 minutes
  },
  
  expandedPanels: [],
  activeFilters: {
    commodityFilter: [],
    probabilityRange: [0, 100],
    confidenceThreshold: 0.5,
    showOnlyHighPriority: false,
    analysisTypes: [],
  },
  exportInProgress: false,
  
  qualityMetrics: null,
  processingInfo: null,
  serviceHealth: [],
};

// Async thunks for data fetching
export const fetchVisualizationData = createAsyncThunk(
  'reportVisualization/fetchVisualizationData',
  async ({ analysisId, includeAll }: { analysisId: string; includeAll?: boolean }) => {
    const params = new URLSearchParams({
      include_visualization: 'true',
      resolution: 'medium',
      ...(includeAll && { include_all: 'true' })
    });
    
    const response = await fetch(`/api/analyses/${analysisId}?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch visualization data: ${response.statusText}`);
    }
    
    return await response.json();
  }
);

export const processTargetRanking = createAsyncThunk(
  'reportVisualization/processTargetRanking',
  async (analyses: any[]) => {
    // Process analyses into target ranking format
    const targets = analyses.map((analysis, index) => ({
      id: analysis.id,
      name: `Target ${index + 1}`,
      probabilityScore: analysis.results?.averageScore || 0,
      confidenceLevel: analysis.results?.averageConfidence || 0,
      estimatedSize: analysis.results?.totalLocations / 1000 || 1, // Convert to km²
      explorationStage: (analysis.results?.highPotentialLocations > 5 ? 'drill_ready' : 
                      analysis.results?.highPotentialLocations > 0 ? 'needs_work' : 'early_stage') as 'drill_ready' | 'needs_work' | 'early_stage',
      commodity: analysis.commodity,
      alterationType: 'mixed',
      nearestAnalog: 'Unknown'
    }));
    
    return {
      targets,
      maxScore: Math.max(...targets.map(t => t.probabilityScore)),
      maxConfidence: Math.max(...targets.map(t => t.confidenceLevel))
    };
  }
);

// Main slice
const reportVisualizationSlice = createSlice({
  name: 'reportVisualization',
  initialState,
  reducers: {
    // View management
    setViewMode: (state, action: PayloadAction<typeof initialState.viewMode>) => {
      state.viewMode = action.payload;
    },
    
    setSelectedAnalysis: (state, action: PayloadAction<string | null>) => {
      state.selectedAnalysisId = action.payload;
      // Clear data when switching analysis
      if (action.payload !== state.selectedAnalysisId) {
        state.processedData = initialState.processedData;
      }
    },

    // Report persistence actions
    setShowDetailedReport: (state, action: PayloadAction<boolean>) => {
      state.showDetailedReport = action.payload;
    },

    setSelectedAnalysisData: (state, action: PayloadAction<any>) => {
      state.selectedAnalysisData = action.payload;
    },

    setAnalysisDetails: (state, action: PayloadAction<any>) => {
      state.analysisDetails = action.payload;
    },

    // Combined action to set all report data at once
    setReportData: (state, action: PayloadAction<{
      analysisId: string;
      analysisData: any;
      analysisDetails: any;
      showDetailed: boolean;
    }>) => {
      state.selectedAnalysisId = action.payload.analysisId;
      state.selectedAnalysisData = action.payload.analysisData;
      state.analysisDetails = action.payload.analysisDetails;
      state.showDetailedReport = action.payload.showDetailed;
    },

    clearReportData: (state) => {
      state.selectedAnalysisId = null;
      state.selectedAnalysisData = null;
      state.analysisDetails = null;
      state.showDetailedReport = false;
    },
    
    addComparisonAnalysis: (state, action: PayloadAction<string>) => {
      if (!state.comparisonAnalysisIds.includes(action.payload)) {
        state.comparisonAnalysisIds.push(action.payload);
      }
    },
    
    removeComparisonAnalysis: (state, action: PayloadAction<string>) => {
      state.comparisonAnalysisIds = state.comparisonAnalysisIds.filter(
        id => id !== action.payload
      );
    },
    
    // Settings management
    setLayerOpacity: (state, action: PayloadAction<{ layerId: string; opacity: number }>) => {
      state.settings.layerOpacity[action.payload.layerId] = action.payload.opacity;
    },
    
    toggleLayerVisibility: (state, action: PayloadAction<string>) => {
      const layerId = action.payload;
      if (state.settings.visibleLayers.includes(layerId)) {
        state.settings.visibleLayers = state.settings.visibleLayers.filter(id => id !== layerId);
      } else {
        state.settings.visibleLayers.push(layerId);
      }
    },
    
    setColorScheme: (state, action: PayloadAction<typeof initialState.settings.colorScheme>) => {
      state.settings.colorScheme = action.payload;
    },
    
    setDataResolution: (state, action: PayloadAction<typeof initialState.settings.dataResolution>) => {
      state.settings.dataResolution = action.payload;
    },
    
    // UI state management
    togglePanel: (state, action: PayloadAction<string>) => {
      const panelId = action.payload;
      if (state.expandedPanels.includes(panelId)) {
        state.expandedPanels = state.expandedPanels.filter(id => id !== panelId);
      } else {
        state.expandedPanels.push(panelId);
      }
    },
    
    updateFilters: (state, action: PayloadAction<Partial<FilterConfiguration>>) => {
      state.activeFilters = { ...state.activeFilters, ...action.payload };
    },
    
    setExportInProgress: (state, action: PayloadAction<boolean>) => {
      state.exportInProgress = action.payload;
    },
    
    // Cache management
    updateCache: (state, action: PayloadAction<{ analysisId: string }>) => {
      state.cache.lastFetch = Date.now();
      if (!state.cache.cachedAnalysisIds.includes(action.payload.analysisId)) {
        state.cache.cachedAnalysisIds.push(action.payload.analysisId);
      }
    },
    
    clearCache: (state) => {
      state.cache.lastFetch = 0;
      state.cache.cachedAnalysisIds = [];
      state.processedData = initialState.processedData;
    },
    
    // Error handling
    clearErrors: (state) => {
      state.errors = initialState.errors;
    },
    
    setError: (state, action: PayloadAction<{ key: keyof typeof initialState.errors; error: string }>) => {
      state.errors[action.payload.key] = action.payload.error;
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Fetch visualization data
      .addCase(fetchVisualizationData.pending, (state) => {
        state.loading.targetRanking = true;
        state.loading.spatialPriority = true;
        state.loading.geologicalContext = true;
        state.errors.targetRanking = null;
      })
      .addCase(fetchVisualizationData.fulfilled, (state, action) => {
        state.loading.targetRanking = false;
        state.loading.spatialPriority = false;
        state.loading.geologicalContext = false;
        
        // Process the received data
        const data = action.payload;
        if (data.visualizationData) {
          // Update quality metrics
          state.qualityMetrics = data.visualizationData.metadata?.dataQuality || null;
          state.processingInfo = data.visualizationData.metadata?.processingInfo || null;
          state.serviceHealth = data.visualizationData.metadata?.apiHealth || [];
        }
        
        // Update cache
        state.cache.lastFetch = Date.now();
      })
      .addCase(fetchVisualizationData.rejected, (state, action) => {
        state.loading.targetRanking = false;
        state.loading.spatialPriority = false;
        state.loading.geologicalContext = false;
        state.errors.targetRanking = action.error.message || 'Failed to fetch data';
      })
      
      // Process target ranking
      .addCase(processTargetRanking.pending, (state) => {
        state.loading.targetRanking = true;
      })
      .addCase(processTargetRanking.fulfilled, (state, action) => {
        state.loading.targetRanking = false;
        state.processedData.targetRanking = action.payload;
      })
      .addCase(processTargetRanking.rejected, (state, action) => {
        state.loading.targetRanking = false;
        state.errors.targetRanking = action.error.message || 'Failed to process target ranking';
      });
  },
});

// Export actions
export const {
  setViewMode,
  setSelectedAnalysis,
  setShowDetailedReport,
  setSelectedAnalysisData,
  setAnalysisDetails,
  setReportData,
  clearReportData,
  addComparisonAnalysis,
  removeComparisonAnalysis,
  setLayerOpacity,
  toggleLayerVisibility,
  setColorScheme,
  setDataResolution,
  togglePanel,
  updateFilters,
  setExportInProgress,
  updateCache,
  clearCache,
  clearErrors,
  setError,
} = reportVisualizationSlice.actions;

// Selectors
export const selectViewMode = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.viewMode;

export const selectSelectedAnalysis = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.selectedAnalysisId;

export const selectTargetRankingData = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.processedData.targetRanking;

export const selectSpatialPriorityData = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.processedData.spatialPriority;

export const selectIsLoading = (state: { reportVisualization: ReportVisualizationState }) => 
  Object.values(state.reportVisualization.loading).some(loading => loading);

export const selectVisibleLayers = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.settings.visibleLayers;

export const selectLayerOpacity = (state: { reportVisualization: ReportVisualizationState }, layerId: string) => 
  state.reportVisualization.settings.layerOpacity[layerId] || 1;

export const selectActiveFilters = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.activeFilters;

export const selectQualityMetrics = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.qualityMetrics;

export const selectServiceHealth = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.serviceHealth;

// Report persistence selectors
export const selectShowDetailedReport = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.showDetailedReport;

export const selectSelectedAnalysisData = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.selectedAnalysisData;

export const selectAnalysisDetails = (state: { reportVisualization: ReportVisualizationState }) => 
  state.reportVisualization.analysisDetails;

export default reportVisualizationSlice.reducer;